import classNames from "classnames";
import "./style.scss";
import { BuilderComponentProps } from "../BuilderInterface";
import {
    getColorsFromInteractiveStates,
    getCompoundStyle,
    getStateColorsForBuilder,
    getStateStyleForBuilder,
    getStylesFromInteractiveStates,
    limitCharacters
} from "../helpers";
import { ButtonNode, NodeStates } from "../../build/types";
import DynamicIcon from "../DynamicIcon";
import { useInteractionStates } from "../../hooks/useInteractionStates";
import { formatTestId } from "../../utils/format";

const BuilderButton: React.FC<BuilderComponentProps> = ({
    node,
    actionCallbacks,
    builderProps,
    dynamicData,
    disabled,
}: BuilderComponentProps) => {
    const { id, attr, stateManager, text } = node;
    const { setElement, states } = useInteractionStates<HTMLButtonElement>();
    const bNode = node as ButtonNode;
    const builderButtonClass = classNames("builder-button", {
        [`${attr?.className}`]: !!attr?.className
    });

    if (builderProps) {
        const { isContainer, nodeState, selectCallback, outlineCallback, onDragStart, onDragEnd } = builderProps;

        return (
            <button
                {...attr}
                id={id}
                data-testid={formatTestId("builder-button", dynamicData || text)}
                className={builderButtonClass}
                style={getCompoundStyle({
                    node,
                    isContainer,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                })}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                onDragStart={e => onDragStart?.(e, id)}
                draggable={"true"}
                onDragEnd={onDragEnd}
            >
                {bNode.icon?.src && (
                    <DynamicIcon
                        name={bNode.icon?.src}
                        style={{
                            ...bNode.icon?.style,
                            pointerEvents: "none",
                            ...getStateStyleForBuilder(stateManager, nodeState, "icon_style")
                        }}
                        colors={getStateColorsForBuilder(stateManager, nodeState, "icon_colors") || bNode.icon.colors}
                    />
                )}
                {bNode.prepend?.value && <span style={bNode.prepend.style}>{bNode.prepend.value}</span>}
                {bNode.dynamicData ? limitCharacters(dynamicData || "", bNode.characterLimit) : text}
                {bNode.append?.value && <span style={bNode.append.style}>{bNode.append.value}</span>}
            </button>
        );
    } else {
        const { style, ...rest } = attr || {};

        const buttonStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({ node }),
            stateManager,
            paths: ["attr_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const prependStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: bNode.prepend?.style,
            stateManager,
            paths: ["prepend_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const appendStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: bNode.append?.style,
            stateManager,
            paths: ["append_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const iconStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: bNode.icon?.style,
            stateManager,
            paths: ["icon_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const iconColors = getColorsFromInteractiveStates({
            states,
            stateManager,
            defaultColors: bNode.icon?.colors,
            paths: ["icon_colors"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        return (
            <button
                ref={setElement}
                id={id}
                data-testid={formatTestId("builder-button", dynamicData || text)}
                {...rest}
                {...actionCallbacks}
                style={buttonStyle}
                className={builderButtonClass}
                disabled={disabled}
            >
                {bNode.icon?.src && <DynamicIcon name={bNode.icon?.src} style={iconStyle} colors={iconColors} />}
                {bNode.prepend?.value && <span style={prependStyle}>{bNode.prepend.value}</span>}
                {bNode.dynamicData ? limitCharacters(dynamicData || "", bNode.characterLimit) : text}
                {bNode.append?.value && <span style={appendStyle}>{bNode.append.value}</span>}
            </button>
        );
    }
};

export default BuilderButton;
